import { Head, router, useForm, usePage } from "@inertiajs/react"
import { Pencil, Plus, Trash2, X } from "lucide-react"
import { useState } from "react"

import { buildMentionHandlesAttributes } from "@/api/mention-handles"
import HeadingSmall from "@/components/heading-small"
import PlatformIcon from "@/components/icons/platform-icon"
import MentionHandleFields from "@/components/mention-handle-fields"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { handleValidationState } from "@/lib/mention-handle-validation"
import { mentionHandleGroupPath, mentionHandleGroupsPath } from "@/routes"
import type {
  BreadcrumbItem,
  MentionHandle,
  MentionHandleGroup,
  Platform,
  SharedProps,
} from "@/types"

interface Props {
  mention_handle_groups: MentionHandleGroup[]
  mention_platforms: Platform[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Mentions", href: mentionHandleGroupsPath() },
]

interface HandleFormData {
  label: string
  fallbackText: string
  handles: Record<string, string>
}

function buildInitialHandles(
  mentionPlatforms: Platform[],
  existing?: MentionHandle[],
): Record<string, string> {
  const result: Record<string, string> = {}
  mentionPlatforms.forEach((platform) => {
    const found = existing?.find((h) => h.platform.name === platform.name)
    result[platform.name] = found?.handle ?? ""
  })
  return result
}

function GroupForm({
  group,
  mentionPlatforms,
  onCancel,
}: {
  group?: MentionHandleGroup
  mentionPlatforms: Platform[]
  onCancel: () => void
}) {
  const pageErrors = (
    usePage<SharedProps>().props as { errors?: Record<string, string[]> }
  ).errors
  const formErrors: string[] = pageErrors?.base ?? []

  const { data, setData, transform, post, patch, processing } =
    useForm<HandleFormData>({
      label: group?.label ?? "",
      fallbackText: group?.fallback_text ?? "",
      handles: buildInitialHandles(mentionPlatforms, group?.mention_handles),
    })
  const hasInvalidHandles = mentionPlatforms.some((platform) => {
    const validationState = handleValidationState(
      platform,
      data.handles[platform.name] ?? "",
    )

    return validationState?.valid === false
  })
  const isSubmitDisabled = processing || hasInvalidHandles

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isSubmitDisabled) return

    transform((current) => ({
      mention_handle_group: {
        label: current.label,
        fallback_text: current.fallbackText,
        mention_handles_attributes: buildMentionHandlesAttributes(
          mentionPlatforms,
          current.handles,
          group,
        ),
      },
    }))

    if (group) {
      patch(mentionHandleGroupPath(group.id), { onSuccess: onCancel })
    } else {
      post(mentionHandleGroupsPath(), { onSuccess: onCancel })
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-xl border p-4"
    >
      {formErrors.length > 0 && (
        <ul className="text-destructive text-sm">
          {formErrors.map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-2">
        <Label htmlFor="mention-label">Label</Label>
        <div className="relative max-w-xs">
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm select-none">
            @
          </span>
          <Input
            id="mention-label"
            value={data.label}
            onChange={(e) => setData("label", e.target.value)}
            placeholder="alice"
            className="pl-7"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="mention-fallback">Fallback text</Label>
        <Input
          id="mention-fallback"
          value={data.fallbackText}
          onChange={(e) => setData("fallbackText", e.target.value)}
          placeholder={data.label ? `@${data.label}` : undefined}
          className="max-w-xs"
        />
        <p className="text-muted-foreground text-xs">
          Swapped in on platforms with no handle for this mention.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium">Handles per platform</p>
        <MentionHandleFields
          mentionPlatforms={mentionPlatforms}
          handles={data.handles}
          onHandleChange={(platformName, value) =>
            setData("handles", { ...data.handles, [platformName]: value })
          }
          placeholderLabel={data.label ? `@${data.label}` : undefined}
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={isSubmitDisabled}>
          {group ? "Update" : "Save"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          <X className="size-4" />
          Cancel
        </Button>
      </div>
    </form>
  )
}

function GroupRow({
  group,
  mentionPlatforms,
  onEdit,
}: {
  group: MentionHandleGroup
  mentionPlatforms: Platform[]
  onEdit: () => void
}) {
  function handleDestroy() {
    router.delete(mentionHandleGroupPath(group.id))
  }

  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
      <div className="flex flex-col gap-1">
        <p className="font-mono font-medium">@{group.label}</p>
        <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {mentionPlatforms.map((platform) => {
            const mentionHandle = group.mention_handles.find(
              (h) => h.platform.name === platform.name,
            )
            return (
              <span
                key={platform.name}
                className="inline-flex items-center gap-1.5"
                aria-label={platform.name}
              >
                <PlatformIcon
                  platform={platform.name}
                  className="text-muted-foreground size-3.5"
                />
                {mentionHandle ? (
                  mentionHandle.profile_url ? (
                    <a
                      href={mentionHandle.profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground decoration-muted-foreground/50 hover:decoration-foreground font-mono underline underline-offset-2"
                    >
                      {mentionHandle.handle}
                    </a>
                  ) : (
                    <span className="text-foreground font-mono">
                      {mentionHandle.handle}
                    </span>
                  )
                ) : (
                  <span className="italic">none</span>
                )}
              </span>
            )
          })}
        </div>
        {group.fallback_text && (
          <p className="text-muted-foreground text-sm">
            Fallback:{" "}
            <span className="text-foreground">{group.fallback_text}</span>
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="icon" onClick={onEdit}>
          <Pencil className="size-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleDestroy}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  )
}

export default function MentionHandleGroupsIndex({
  mention_handle_groups,
  mention_platforms,
}: Props) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Mentions" />

      <div className="space-y-6 px-4 py-6">
        <HeadingSmall
          title="Mentions"
          description="Define per-platform handles for @mentions used in your takes."
        />

        <div className="flex flex-col gap-3">
          {mention_handle_groups.map((group) =>
            editingId === group.id ? (
              <GroupForm
                key={group.id}
                group={group}
                mentionPlatforms={mention_platforms}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <GroupRow
                key={group.id}
                group={group}
                mentionPlatforms={mention_platforms}
                onEdit={() => setEditingId(group.id)}
              />
            ),
          )}

          {showAddForm ? (
            <GroupForm
              mentionPlatforms={mention_platforms}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="size-4" />
                Add mention
              </Button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
